// src/products/analysis/pages/CrowdfundingPage.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  BarChart3,
  Building,
  Calendar,
  DollarSign,
  Filter,
  HelpCircle,
  Loader2,
  PieChart,
  Rocket,
  TrendingUp
} from 'lucide-react';
import { getCrowdfundingFilings } from '../services/secService';

const dayOptions = [
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 60 days', value: 60 },
  { label: 'Last 90 days', value: 90 },
  { label: 'Last 180 days', value: 180 }
];

const helpfulTips = [
  'Form C filings (Reg CF) disclose target raise amounts, portals, and minimum investments.',
  'Revisit filings after the campaign closes (Form C-U) to compare actual vs. target amounts.',
  'Use the CIK to open the Private Filings tab and download accompanying pitch decks or financials.',
  'Portals like Wefunder and StartEngine list active campaigns—cross-link your research with their public pages.'
];

const CrowdfundingPage = () => {
  const [days, setDays] = useState(30);
  const [limit] = useState(60);
  const [portal, setPortal] = useState('all');
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [availablePortals, setAvailablePortals] = useState([]);

  const loadFilings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getCrowdfundingFilings({
        days,
        limit,
        portal: portal === 'all' ? undefined : portal
      });

      const results = response.filings || [];
      setFilings(results);

      if (portal === 'all') {
        const portals = Array.from(
          new Set(
            results
              .map((item) => item.portalName)
              .filter((name) => Boolean(name && name.trim()))
          )
        ).sort();
        setAvailablePortals(portals);
      }
    } catch (err) {
      console.error('Crowdfunding fetch error:', err);
      setError(err.message || 'Failed to load crowdfunding filings.');
      setFilings([]);
    } finally {
      setLoading(false);
    }
  }, [days, limit, portal]);

  useEffect(() => {
    loadFilings();
  }, [loadFilings]);

  const summary = useMemo(() => {
    if (!filings.length) {
      return {
        totalTarget: 0,
        totalRaised: 0,
        avgMinimum: 0,
        portals: 0
      };
    }

    const totals = filings.reduce(
      (acc, filing) => {
        const offering = filing.offeringData || {};
        acc.totalTarget += Number(offering.totalOfferingAmount || 0);
        acc.totalRaised += Number(offering.totalAmountSold || 0);
        acc.minimumInvestments.push(Number(offering.minimumInvestment || 0));
        acc.portals.add((filing.portalName || '').trim());
        return acc;
      },
      {
        totalTarget: 0,
        totalRaised: 0,
        minimumInvestments: [],
        portals: new Set()
      }
    );

    const filteredMinimums = totals.minimumInvestments.filter((value) => value > 0);
    const avgMinimum = filteredMinimums.length
      ? filteredMinimums.reduce((sum, value) => sum + value, 0) / filteredMinimums.length
      : 0;

    return {
      totalTarget: totals.totalTarget,
      totalRaised: totals.totalRaised,
      avgMinimum,
      portals: Array.from(totals.portals).filter(Boolean).length
    };
  }, [filings]);

  const formatCurrency = (value) => {
    if (!value || Number.isNaN(value)) return '—';
    if (value >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(1)}M`;
    }
    if (value >= 1_000) {
      return `$${(value / 1_000).toFixed(1)}K`;
    }
    return `$${value.toLocaleString()}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Crowdfunded Offerings</h1>
          <p className="text-sm text-gray-600 mt-1">
            Monitor recent Regulation Crowdfunding filings (Form C & C-U) to track private company raises across leading portals.
          </p>
        </div>
        <div className="hidden md:flex items-center gap-2 text-xs text-gray-500">
          <HelpCircle className="w-4 h-4" />
          Data sourced live from SEC EDGAR search index (free tier, no key required).
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5 space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <Filter className="w-4 h-4" />
            <span className="font-medium text-gray-700">Filters</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <select
              value={days}
              onChange={(event) => setDays(Number(event.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              {dayOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <select
              value={portal}
              onChange={(event) => setPortal(event.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              <option value="all">All Funding Portals</option>
              {availablePortals.map((portalName) => (
                <option key={portalName} value={portalName}>
                  {portalName}
                </option>
              ))}
            </select>
            <button
              onClick={loadFilings}
              className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
            </button>
          </div>
        </div>
        {error && (
          <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertCircle className="w-4 h-4 mt-0.5" />
            <div>
              <p className="font-medium">Unable to load filings</p>
              <p>{error}</p>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <TrendingUp className="w-4 h-4" />
            Total Target
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {formatCurrency(summary.totalTarget)}
          </div>
          <p className="text-xs text-gray-500 mt-1">Aggregate target across filings in view.</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <DollarSign className="w-4 h-4" />
            Capital Raised
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {formatCurrency(summary.totalRaised)}
          </div>
          <p className="text-xs text-gray-500 mt-1">Reported sold amounts from Form C-U updates.</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <PieChart className="w-4 h-4" />
            Avg Minimum Investment
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {summary.avgMinimum ? `$${summary.avgMinimum.toFixed(0)}` : '—'}
          </div>
          <p className="text-xs text-gray-500 mt-1">Mean minimum ticket across current filings.</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-gray-600 text-sm">
            <BarChart3 className="w-4 h-4" />
            Unique Portals
          </div>
          <div className="text-2xl font-bold text-gray-900 mt-2">
            {summary.portals}
          </div>
          <p className="text-xs text-gray-500 mt-1">Distinct funding portals represented.</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between px-5 py-4 border-b border-gray-200 gap-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Latest Crowdfunding Filings
            </h2>
            <p className="text-xs text-gray-500">
              Showing {filings.length} results. Click documents to open full Form C packages on sec.gov.
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Company
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Portal
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Filed
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Target / Raised
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Min Ticket
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Documents
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                    Loading filings…
                  </td>
                </tr>
              )}
              {!loading && filings.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500">
                    No filings found for the selected filters. Try expanding the time range or clearing the portal filter.
                  </td>
                </tr>
              )}
              {!loading &&
                filings.map((filing) => {
                  const offering = filing.offeringData || {};
                  return (
                    <tr key={filing.accessionNumber || `${filing.companyName}-${filing.filedAt}`}>
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-900">{filing.companyName}</span>
                          <span className="text-xs text-gray-500">
                            {filing.formType} &middot; CIK {filing.companyCik || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {filing.portalName || '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {filing.filedAt ? new Date(filing.filedAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900">
                            Target: {formatCurrency(Number(offering.totalOfferingAmount || offering.targetOfferingAmount))}
                          </span>
                          <span className="text-xs text-gray-500">
                            Raised: {formatCurrency(Number(offering.totalAmountSold))}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                        {offering.minimumInvestment ? `$${Number(offering.minimumInvestment).toLocaleString()}` : '—'}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-blue-600">
                        <div className="flex items-center gap-3 text-xs">
                          {filing.links?.html && (
                            <a
                              href={filing.links.html}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline flex items-center gap-1"
                            >
                              <Rocket className="w-3 h-3" /> HTML
                            </a>
                          )}
                          {filing.links?.details && (
                            <a
                              href={filing.links.details}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline flex items-center gap-1"
                            >
                              <Calendar className="w-3 h-3" /> Details
                            </a>
                          )}
                          {filing.links?.txt && (
                            <a
                              href={filing.links.txt}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline flex items-center gap-1"
                            >
                              <Building className="w-3 h-3" /> TXT
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-5">
        <div className="flex items-center gap-2 text-gray-700 font-medium text-sm mb-3">
          <HelpCircle className="w-4 h-4" />
          Helpful Hints
        </div>
        <ul className="list-disc list-inside text-sm text-gray-600 space-y-2">
          {helpfulTips.map((tip) => (
            <li key={tip}>{tip}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default CrowdfundingPage;














